import React from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { useAuthStore, useRoutesStore } from 'store';

import { SOFTWARE_MANAGEMENT_ROUTES } from '../../index';

interface IAccountNavigationOptions {
    accountUuid?: string;
}

interface IComponentVersionNavigationOptions extends IAccountNavigationOptions {
    componentUuid: string;
}

interface IComponentVersionEditingNavigationOptions extends IComponentVersionNavigationOptions {
    versionUuid: string;
}

interface IRaptorComponentNavigator {
    toComponentCreationPage: (options: IAccountNavigationOptions) => void;
    toComponentListingPage: (options: IAccountNavigationOptions) => void;
    toComponentVersionCreationPage: (options: IComponentVersionNavigationOptions) => void;
    toComponentVersionEditingPage: (options: IComponentVersionEditingNavigationOptions) => void;
}

export const useRaptorComponentNavigator = (): IRaptorComponentNavigator => {
    const navigate = useNavigate();
    const { isDPUAdmin } = useAuthStore();
    const routeStore = useRoutesStore();
    const componentCreateRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENTS_CREATE);
    const componentListRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENTS);
    const componentVersionCreateRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENT_VERSIONS_CREATE);
    const componentVersionEditRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENT_VERSIONS_EDIT);

    const getAccountSearch = React.useCallback((accountUuid?: string): string | undefined => {
        if (!isDPUAdmin || !accountUuid) {
            return undefined;
        }

        return `?${new URLSearchParams({ accountUuid }).toString()}`;
    }, [isDPUAdmin]);

    return {
        toComponentCreationPage: React.useCallback(
            ({ accountUuid }): void => {
                navigate({
                    pathname: componentCreateRoute.path,
                    search: getAccountSearch(accountUuid),
                });
            },
            [componentCreateRoute.path, getAccountSearch, navigate],
        ),
        toComponentListingPage: React.useCallback(
            ({ accountUuid }): void => {
                navigate({
                    pathname: componentListRoute.path,
                    search: getAccountSearch(accountUuid),
                });
            },
            [componentListRoute.path, getAccountSearch, navigate],
        ),
        toComponentVersionCreationPage: React.useCallback(
            ({ componentUuid, accountUuid }): void => {
                navigate({
                    pathname: generatePath(componentVersionCreateRoute.path, { componentUuid }),
                    search: getAccountSearch(accountUuid),
                });
            },
            [componentVersionCreateRoute.path, getAccountSearch, navigate],
        ),
        toComponentVersionEditingPage: React.useCallback(
            ({ componentUuid, versionUuid, accountUuid }): void => {
                navigate({
                    pathname: generatePath(componentVersionEditRoute.path, { componentUuid, versionUuid }),
                    search: getAccountSearch(accountUuid),
                });
            },
            [componentVersionEditRoute.path, getAccountSearch, navigate],
        ),
    };
};
